import { Link } from "react-router-dom";
import { Star, Folder, Heart, Timer, Pill, Phone, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import ProtocolCard from "@/components/protocol/ProtocolCard";
import CrossServiceCompare from "@/components/protocol/CrossServiceCompare";
import { iconMap, careLevels, CARE_LEVEL_ORDER, SFA_EMR_CARE_LEVEL_ORDER, WES_CARE_LEVEL_ORDER, AIR_CARE_LEVEL_ORDER } from "@/config/constants";
import { useService } from "@/contexts/ServiceContext";
import { useOfflineData } from "@/contexts/OfflineDataContext";

const SERVICE_HERO = {
  ground: {
    title: "Yukon EMS Clinical Guidelines",
    subtitle: "Evidence-based protocols for PCP, PCP-ES, and ACP-G practitioners. Quick access to life-saving procedures.",
    levels: CARE_LEVEL_ORDER,
  },
  sfa_emr: {
    title: "SFA/EMR Clinical Guidelines",
    subtitle: "Protocols for Standard First Aid and Emergency Medical Responder practitioners.",
    levels: SFA_EMR_CARE_LEVEL_ORDER,
  },
  wes: {
    title: "PCP-PS Paramedic Specialist Guidelines",
    subtitle: "Clinical guidelines for Paramedic Specialists (PCP-PS) operating in the Whitehorse Emergency Shelter environment.",
    levels: WES_CARE_LEVEL_ORDER,
  },
  air_adult: {
    title: "Air Ambulance – Adult Guidelines",
    subtitle: "Clinical guidelines for ACP-F and CCP practitioners in the air ambulance environment.",
    levels: AIR_CARE_LEVEL_ORDER,
  },
  air_pediatric: {
    title: "Air Ambulance – OB/NEO/PEDS Guidelines",
    subtitle: "Clinical guidelines for Obstetrics, Neonatal, and Pediatric patients in the air ambulance environment.",
    levels: AIR_CARE_LEVEL_ORDER,
  },
};

export default function Dashboard({ protocols, categories, favorites, onToggleFavorite }) {
  const { activeService } = useService();
  const { syncing, syncAllData } = useOfflineData();
  const hero = SERVICE_HERO[activeService] || SERVICE_HERO.ground;

  const handleForceRefresh = async () => {
    // Clear all service worker caches to bust stale data
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(name => caches.delete(name)));
    }
    // Force a fresh sync from the network
    await syncAllData(true);
    // If still empty after sync, hard reload
    window.location.reload();
  };

  // Filter categories and protocols by active service
  const filteredCategories = categories.filter(cat => (cat.service || "ground") === activeService);
  const filteredProtocols = protocols.filter(p => (p.service || "ground") === activeService);

  const recentFavorites = favorites.slice(0, 4);
  const favoriteProtocols = filteredProtocols.filter(p => recentFavorites.some(f => f.protocol_id === p.id));

  // Get care levels to display in legend
  const legendLevels = hero.levels.map(key => [key, careLevels[key]]).filter(([, v]) => v);

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary/10 via-background to-background border border-border p-6 md:p-8">
        <div className="relative z-10">
          <h1 className="font-heading text-3xl md:text-4xl font-bold" data-testid="dashboard-title">{hero.title}</h1>
          <p className="text-muted-foreground mt-2 max-w-xl">{hero.subtitle}</p>
        </div>
        <div className="absolute right-0 top-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
      </div>

      {/* Code Timer Quick Access */}
      <Link to="/code-timer">
        <Card className="border-red-500/30 hover:border-red-500/60 transition-all hover:shadow-md cursor-pointer bg-red-500/5" data-testid="dashboard-code-timer">
          <CardContent className="pt-5 pb-4 flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-red-500/15 flex items-center justify-center shrink-0">
              <Heart className="w-7 h-7 text-red-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-lg">Code Timer</h3>
              <p className="text-sm text-muted-foreground">CPR cycles, Epi & Amiodarone timers, 110 BPM metronome</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Badge variant="outline" className="bg-primary/10 border-primary/30"><Timer className="w-3 h-3 mr-1" />2 min</Badge>
              <Badge variant="outline" className="bg-green-500/10 border-green-500/30 text-green-400"><Pill className="w-3 h-3 mr-1" />Epi</Badge>
            </div>
          </CardContent>
        </Card>
      </Link>

      {/* Quick Access - Favorites */}
      {favoriteProtocols.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Quick Access
            </h2>
            <Link to="/favorites">
              <Button variant="ghost" size="sm">View all</Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {favoriteProtocols.map(protocol => (
              <ProtocolCard 
                key={protocol.id} 
                protocol={protocol} 
                isFavorite={true}
                onToggleFavorite={onToggleFavorite}
              />
            ))}
          </div>
        </section>
      )}

      {/* Categories Grid */}
      <section>
        <h2 className="font-heading text-xl font-semibold mb-4">Protocol Categories</h2>
        {filteredCategories.filter(c => c.protocol_count > 0).length === 0 && syncing ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="categories-loading">
            <Loader2 className="w-8 h-8 animate-spin mb-3 text-primary" />
            <p className="text-sm font-medium">Loading protocols...</p>
            <p className="text-xs mt-1">Syncing clinical data for offline use</p>
          </div>
        ) : filteredCategories.filter(c => c.protocol_count > 0).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="categories-empty">
            <p className="text-sm">No categories found for this service.</p>
            <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={handleForceRefresh}>
              <RefreshCw className="w-3.5 h-3.5" />
              Force Refresh Data
            </Button>
          </div>
        ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredCategories.filter(c => c.protocol_count > 0).map(category => {
            // Handle case-insensitive icon lookup (WES uses 'Activity', Ground uses 'activity')
            const iconKey = category.icon?.toLowerCase();
            const Icon = iconMap[iconKey] || Folder;
            return (
              <Link key={category.id} to={`/category/${category.id}`}>
                <Card className="hover:border-primary/50 transition-all hover:shadow-md cursor-pointer h-full" data-testid={`category-card-${category.id}`}>
                  <CardContent className="pt-6 flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-medium">{category.name}</h3>
                    <p className="text-sm text-muted-foreground">{category.protocol_count} protocols</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
        )}
      </section>

      {/* Care Level Legend */}
      <section>
        <h2 className="font-heading text-xl font-semibold mb-4">Care Level Legend</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {legendLevels.map(([key, config]) => (
            <div key={key} className={`p-3 rounded-lg border care-badge-${key} border-current`} data-testid={`legend-${key}`}>
              <div className="font-semibold">{config.shortLabel}</div>
              <div className="text-xs opacity-80">{config.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Cross-Service Compare */}
      <section>
        <CrossServiceCompare />
      </section>
    </div>
  );
}
